import { reversedStrip } from "../assets/animation.ts";
import { frameId } from "../assets/manifest.ts";
import { COLLISION_CELLS_PER_TILE, type MapDocument, type PropInstance, type TileRef, type WorldDocument } from "../document/types.ts";
import type { CompileContext, CompileObjectType, FrameDimensions, TransitionStrip } from "./context.ts";
import { footprintFromCells } from "./footprint-override.ts";
import { footprintBlocks, type PropFootprint } from "./footprint.ts";
import { drawnFrameAnchor } from "./drawn-frame.ts";
import { propFeet } from "../editor/objects.ts";
import { tieKeys } from "../editor/sort.ts";
import type { ManifestAppearanceState, ManifestAppearanceTransition, ManifestObjectAppearance, ManifestPixelOffset } from "./manifest.ts";

/**
 * Lowering a behaviour prop (ADR-053). A prop that carries a behaviour stays a prop in the document; the
 * compiler resolves what it looks like, how it animates between states (ADR-058) and what it blocks into an
 * appearance, one entry per distinct set of frames, transition strips, footprints, activation cells and
 * mirroring, and, for a thing, where it draws (P1.1-T7), and the emitted entity carries only its index. The
 * map server stamps and tests from the table without the document, and the wire carries the index in the
 * snapshot's `appearance` field. A prop whose type is driven by presence (ADR-058 amendment, P1.3-T4)
 * resolves to an appearance naming its inside state but is never lowered: it stays a prop in its chunk's
 * prop layer carrying the index, and each viewer's client drives it.
 */

/**
 * The appearances a world's behaviour props resolve to, and which entry each prop takes.
 */
export interface AppearanceTable {
  readonly appearances: readonly ManifestObjectAppearance[];
  readonly indexOf: ReadonlyMap<PropInstance, number>;
}

/**
 * Largest appearance index the snapshot's `u16 appearance` field carries.
 */
const MAX_APPEARANCE = 65535;

/**
 * The cells a thing is usable from when its behaviour names none (ADR-053 decision 8, as amended by
 * P1.8-T3): the bounding box of the cells every footprint the type can take blocks, grown by one cell on
 * every side, minus the cells this state's own footprint blocks. Spanning the other states is what makes a
 * state usable from beside the shape it changes into: an open gate's ring around its posts alone would end
 * inside the closed span behind them, where no closer can stand clear of it. Each other footprint is placed
 * by the frame's centre; when it lies half a cell off this state's grid the stamp lays it on whichever
 * neighbouring cell its frame's corner rounds to, which depends on where the thing stands, so both are
 * spanned. Rows run up from the frame's bottom, so row -1 lies in front of the art.
 *
 * @param footprint The state's resolved footprint, null when it stamps nothing.
 * @param tileSize Tile size in pixels.
 * @param cellsPerTile Collision cells per tile.
 * @param others The footprints of the type's other states, null for one that stamps nothing.
 * @returns The cells, row-major from the bottom; empty when the state blocks nothing to grow around.
 */
export function defaultActivation(footprint: PropFootprint | null, tileSize: number, cellsPerTile: number, others: readonly (PropFootprint | null)[] = []): (readonly [number, number])[] {
  if (footprint === null) {
    return [];
  }
  const cells = footprintBlocks(footprint, tileSize / cellsPerTile);
  if (cells.length === 0) {
    return [];
  }
  const cellPixels = tileSize / cellsPerTile;
  const spanned = [...cells];
  for (const other of others) {
    if (other === null) {
      continue;
    }
    const shift = (footprint.w - other.w) / (2 * cellPixels);
    for (const [c, r] of footprintBlocks(other, tileSize / cellsPerTile)) {
      spanned.push([c + Math.floor(shift), r], [c + Math.ceil(shift), r]);
    }
  }
  const columns = spanned.map(([c]) => c);
  const rows = spanned.map(([, r]) => r);
  const occupied = new Set(cells.map(([c, r]) => `${c},${r}`));
  const out: (readonly [number, number])[] = [];
  for (let r = Math.min(...rows) - 1; r <= Math.max(...rows) + 1; r += 1) {
    for (let c = Math.min(...columns) - 1; c <= Math.max(...columns) + 1; c += 1) {
      if (!occupied.has(`${c},${r}`)) {
        out.push([c, r]);
      }
    }
  }
  return out;
}

/**
 * The frame a state shows: the prop's own frame for the initial state, the behaviour's frame for a further
 * state. A further state the author has not given a frame refuses the compile rather than borrowing the
 * initial state's art, since a thing whose states look alike reads as broken (ADR-053); the editor reports
 * the same thing as an issue on the prop.
 *
 * @param prop The placed prop.
 * @param type The object type.
 * @param stateId The state.
 * @returns The frame.
 * @throws Error when a further state has no frame.
 */
function frameOf(prop: PropInstance, type: CompileObjectType, stateId: string): TileRef {
  if (stateId === type.initial) {
    return prop.frame;
  }
  const frame = prop.behaviour?.frames?.[stateId];
  if (frame === undefined) {
    throw new Error(`prop ${prop.id} carries behaviour ${type.type} whose ${stateId} state has no frame; pick one in its Behaviour section`);
  }
  return frame;
}

/**
 * A frame's size as the atlas cuts it (P1.1-T6): the context's frame size or, in a context written before
 * frame sizes were carried, the size of the frame's footprint, which is measured over the same image. A frame
 * the game's footprint policy leaves without a footprint is sized all the same. The editor's behaviour checks
 * size a thing by this too, so they stand it where the compile does.
 *
 * @param context The compile context.
 * @param id The frame id.
 * @returns The size, or undefined when the context can size the frame neither way.
 */
export function frameSizeOf(context: Pick<CompileContext, "collision" | "frameSizes">, id: string): FrameDimensions | undefined {
  return context.frameSizes?.[id] ?? context.collision.footprints?.[id];
}

/**
 * Whether a state's footprint can be resolved: always for `auto` and `none`, and for cells only when the
 * frame they are laid on can be sized.
 *
 * @param state The state and its policy.
 * @param size The frame's size, undefined when the context cannot size it.
 * @returns True when `footprintOf` answers rather than throws.
 */
export function resolvable(state: CompileObjectType["states"][number], size: FrameDimensions | undefined): boolean {
  return typeof state.footprint === "string" || size !== undefined;
}

/**
 * The footprint a state stamps: the frame's derived footprint for `auto`, nothing for `none`, or the
 * state's cells laid on the frame's rect.
 *
 * @param prop The placed prop, for error messages.
 * @param state The state and its policy.
 * @param id The id of the frame the state shows, for error messages.
 * @param derived The frame's derived footprint, undefined when the frame has none.
 * @param size The frame's size, undefined when the context cannot size it.
 * @param cellPixels Pixels per collision cell.
 * @returns The footprint, or null when the state stamps nothing.
 * @throws Error when the state names cells but the context cannot size its frame.
 */
export function footprintOf(prop: PropInstance, state: CompileObjectType["states"][number], id: string, derived: PropFootprint | undefined, size: FrameDimensions | undefined, cellPixels: number): PropFootprint | null {
  if (state.footprint === "none") {
    return null;
  }
  if (state.footprint === "auto") {
    return derived ?? null;
  }
  if (size === undefined) {
    throw new Error(`prop ${prop.id} shows frame ${id} in its ${state.id} state, which none of the world's packs provides, so its footprint cells cannot be laid on it; choose another frame in its Behaviour section`);
  }
  return footprintFromCells(state.footprint.cells, size.w, size.h, cellPixels);
}

/**
 * The activation cells of one state, mirrored with the prop across the columns of the state's frame rect so
 * a flipped thing is usable from the mirrored side.
 *
 * @param prop The placed prop.
 * @param state The state's id, the id of the frame it shows, that frame's size and the state's resolved
 *   footprint.
 * @param others The footprints of the type's other states, which the default ring spans.
 * @param tileSize Tile size in pixels.
 * @returns The cells.
 * @throws Error when the behaviour names activation cells but the context cannot size the state's frame.
 */
export function activationOf(prop: PropInstance, state: { readonly id: string; readonly frameId: string; readonly size: FrameDimensions | undefined; readonly footprint: PropFootprint | null }, others: readonly (PropFootprint | null)[], tileSize: number): (readonly [number, number])[] {
  const cellPixels = tileSize / COLLISION_CELLS_PER_TILE;
  const authored = prop.behaviour?.activation;
  if (authored !== undefined && state.size === undefined) {
    throw new Error(`prop ${prop.id} names activation cells, but frame ${state.frameId} of its ${state.id} state is in none of the world's packs, so they cannot be placed; choose another frame in its Behaviour section or clear the cells in its Activation section`);
  }
  const cells = authored === undefined ? defaultActivation(state.footprint, tileSize, COLLISION_CELLS_PER_TILE, others) : authored.map(([c, r]) => [c, r] as const);
  if (!prop.flipX || state.size === undefined) {
    return cells;
  }
  const columns = Math.ceil(state.size.w / cellPixels);
  return cells.map(([c, r]) => [columns - 1 - c, r] as const);
}

/**
 * The area of a prop driven by presence (ADR-058 amendment, P1.3-T4): its painted cells in its placed
 * frame's space, mirrored with the prop across the columns of that frame's rect, as a thing's cells are
 * (P1.1-T6), one set for every state so nothing flips between two shapes. With none painted, or an empty
 * list, it carries none, and the client derives the ground under the art from its asset manifest, since the
 * compiler knows no art bounds. Only a mirrored prop needs its frame's size, which a frame with no footprint
 * has as well. The editor takes a presence prop's painted cells from here too (ADR-071 decision 5), so they
 * are mirrored exactly as the game receives them: on `flipX` alone.
 *
 * @param prop The placed prop.
 * @param placed The id of the placed frame and its size, undefined when the context cannot size it.
 * @param tileSize Tile size in pixels.
 * @returns The cells.
 * @throws Error when the prop is mirrored and names cells but the context cannot size its frame.
 */
export function presenceCells(prop: PropInstance, placed: { readonly frameId: string; readonly size: FrameDimensions | undefined }, tileSize: number): (readonly [number, number])[] {
  const authored = prop.behaviour?.activation ?? [];
  if (!prop.flipX || authored.length === 0) {
    return authored.map(([c, r]) => [c, r] as const);
  }
  if (placed.size === undefined) {
    throw new Error(`prop ${prop.id} is mirrored and names activation cells, but its frame ${placed.frameId} is in none of the world's packs, so they cannot be mirrored; clear the cells in its Activation section`);
  }
  const columns = Math.ceil(placed.size.w / (tileSize / COLLISION_CELLS_PER_TILE));
  return authored.map(([c, r]) => [columns - 1 - c, r] as const);
}

/**
 * The transition animations of a behaviour prop (ADR-058 decision 5), keyed by the state indices a client
 * sees change: for each ordered pair of different states, the first declared transition between them that
 * plays an animation the prop names, expanded into that one-shot strip's frames, last to first for a
 * transition the type marks `reverse` (ADR-058's amendment of 2026-10-03), so a client plays every entry
 * first frame to last and knows no direction. A transition whose animation the author left empty carries no
 * entry, so the client cuts; a self-transition changes no state index and never plays.
 *
 * @param prop The placed prop.
 * @param type The object type its behaviour names.
 * @param strips The catalogue's one-shot strips by the frame id of their first frame.
 * @returns The transitions, in declaration order.
 * @throws Error when an animation the prop names starts no one-shot strip.
 */
function transitionsOf(prop: PropInstance, type: CompileObjectType, strips: Readonly<Record<string, TransitionStrip>>): ManifestAppearanceTransition[] {
  const out: ManifestAppearanceTransition[] = [];
  const taken = new Set<string>();
  for (const transition of type.transitions ?? []) {
    const from = type.states.findIndex((s) => s.id === transition.from);
    const to = type.states.findIndex((s) => s.id === transition.to);
    const chosen = prop.behaviour?.animations?.[transition.plays];
    const pair = `${from} ${to}`;
    if (from === to || from < 0 || to < 0 || chosen === undefined || taken.has(pair)) {
      continue;
    }
    const start = frameId(chosen.tileset, null, chosen.tile);
    const strip = strips[start];
    if (strip === undefined) {
      throw new Error(`prop ${prop.id} animation ${transition.plays} names frame ${start}, which starts no one-shot strip; choose another or None in its Behaviour section`);
    }
    taken.add(pair);
    const played = transition.reverse === true ? reversedStrip(strip) : strip;
    out.push({ from, to, frames: played.frames.map((f) => ({ tileset: f.tileset, tile: f.tile })), durations: [...played.durations] });
  }
  return out;
}

/**
 * The pixels from the anchor `objectAnchor` gives over a thing's feet point, as a server holding positions at
 * `positionsPerTile` units a tile rounds it, to the author's anchor (P1.1-T7). The server rounds the compiled
 * feet point to the nearest unit, halves upward; the anchor moves by exactly what the rounding moves the feet
 * point by, so the offset is that difference, reversed.
 *
 * @param prop The placed prop.
 * @param width The pixel width of the frame its initial state shows, which the feet point is centred on.
 * @param tileSize Tile size in pixels.
 * @param positionsPerTile The server's grain, or undefined when the feet point is held exactly.
 * @returns The offset in pixels, zero on both axes when the feet point lies on the grain.
 */
function pixelOffset(prop: PropInstance, width: number, tileSize: number, positionsPerTile: number | undefined): ManifestPixelOffset {
  if (positionsPerTile === undefined) {
    return { x: 0, y: 0 };
  }
  const feet = propFeet(prop, width, tileSize);
  const units = { x: feet.x * positionsPerTile, y: feet.y * positionsPerTile };
  return { x: ((units.x - Math.round(units.x)) * tileSize) / positionsPerTile, y: ((units.y - Math.round(units.y)) * tileSize) / positionsPerTile };
}

/**
 * The appearance a behaviour prop resolves to. Each state is as wide as the rect of the frame it shows, which
 * the lowered feet point, the activation cells and their mirroring are laid out by, whether or not that frame
 * has a footprint (P1.1-T6). A prop driven by presence (ADR-058 amendment, P1.3-T4) names its inside state and
 * carries its area on every state in place of an activation ring. A thing, every other behaviour prop, also
 * carries where it draws (P1.1-T7): its layer's compiled index, its sort offset, its pixel offset and its tie
 * key in its layer (P1.6-T42).
 *
 * @param prop The placed prop.
 * @param type The object type its behaviour names.
 * @param context The compile context, for the derived footprints, the frame sizes and the server's grain.
 * @param strips The catalogue's one-shot strips by the frame id of their first frame.
 * @param tileSize Tile size in pixels.
 * @param layer The index of the prop's layer among its map's compiled layers.
 * @param order The prop's tie key in its layer.
 * @returns The appearance.
 * @throws Error when a state's cells or activation cells cannot be sized, or an animation the prop names
 *   starts no one-shot strip.
 */
function appearanceOf(prop: PropInstance, type: CompileObjectType, context: CompileContext, strips: Readonly<Record<string, TransitionStrip>>, tileSize: number, layer: number, order: number): ManifestObjectAppearance {
  const footprints = context.collision.footprints ?? {};
  const resolved = type.states.map((state) => {
    const frame = frameOf(prop, type, state.id);
    const id = frameId(frame.tileset, null, frame.tile);
    const size = frameSizeOf(context, id);
    return { id: state.id, frame, frameId: id, size, footprint: footprintOf(prop, state, id, footprints[id], size, tileSize / COLLISION_CELLS_PER_TILE) };
  });
  const initial = Math.max(0, type.states.findIndex((s) => s.id === type.initial));
  const placed = resolved[initial];
  const area = type.inside === undefined || placed === undefined ? null : presenceCells(prop, placed, tileSize);
  const states: ManifestAppearanceState[] = resolved.map((state, i) => {
    const others = resolved.filter((_, j) => j !== i).map((s) => s.footprint);
    return { id: state.id, frame: state.frame, width: state.size?.w ?? 0, footprint: state.footprint, activation: area ?? activationOf(prop, state, others, tileSize) };
  });
  const transitions = transitionsOf(prop, type, strips);
  const inside = type.inside === undefined ? -1 : type.states.findIndex((s) => s.id === type.inside);
  const look = { states, ...(transitions.length === 0 ? {} : { transitions }), initial, ...(inside < 0 ? {} : { inside }), flipX: prop.flipX, flipY: prop.flipY };
  const named = type.soundSet === undefined ? undefined : prop.behaviour?.fields[type.soundSet.field];
  const soundSet = type.soundSet !== undefined && typeof named === "string" && named !== type.soundSet.default && type.soundSet.options.includes(named) ? named : undefined;
  const sounding = soundSet === undefined ? look : { ...look, soundSet };
  return type.inside === undefined ? { ...sounding, layer, sortPixels: prop.sortPixels, offset: pixelOffset(prop, placed?.size?.w ?? 0, tileSize, context.positionsPerTile), order } : sounding;
}

/**
 * A canonical key for an appearance, so two props that look, animate and block alike share one entry and a
 * recompilation of an unchanged document yields the same table.
 *
 * @param appearance The appearance.
 * @returns The key.
 */
function appearanceKey(appearance: ManifestObjectAppearance): string {
  return JSON.stringify([
    appearance.flipX,
    appearance.flipY,
    appearance.initial,
    appearance.inside ?? null,
    appearance.states.map((s) => [s.id, s.frame.tileset, s.frame.tile, s.width, s.footprint, s.activation]),
    (appearance.transitions ?? []).map((t) => [t.from, t.to, t.frames.map((f) => [f.tileset, f.tile]), t.durations]),
    appearance.layer ?? null,
    appearance.sortPixels ?? null,
    appearance.offset === undefined ? null : [appearance.offset.x, appearance.offset.y],
    appearance.order ?? null,
    appearance.soundSet ?? null,
  ]);
}

/**
 * The feet point a thing stands on in the game: the bottom centre of its initial state's frame, rounded to the
 * grain the server holds a lowered thing at when the context names one, as the compile's pixel offset rounds
 * it. It is where the entity is, and the point the editor marks; a thing's footprint and activation cells are laid from
 * its art instead (`thingAnchor`).
 *
 * @param prop The placed prop.
 * @param width The pixel width of its initial state's frame.
 * @param context The compile context, for the server's grain.
 * @param tileSize Tile size in pixels.
 * @returns The feet point in tiles.
 */
export function thingFeet(prop: PropInstance, width: number, context: Pick<CompileContext, "positionsPerTile">, tileSize: number): { readonly x: number; readonly y: number } {
  const feet = propFeet(prop, width, tileSize);
  const grain = context.positionsPerTile;
  return grain === undefined ? feet : { x: Math.round(feet.x * grain) / grain, y: Math.round(feet.y * grain) / grain };
}

/**
 * Where a behaviour prop's state frame is drawn, the anchor its footprint and its activation cells are laid
 * from (ADR-040 amendment of 2026-10-04, P1.6-T83): the frame of the state's width centred on the bottom
 * centre of the placed frame, at the author's pixel, with no rounding to the grain the server holds the feet
 * point at. The game arrives at the same anchor from the rounded feet point and the appearance's pixel offset
 * (`drawnFrameAnchor`), so the editor's overlay and play lay a thing on the same cells, the ones nearest its
 * art. A state whose frame is as wide as the placed one, and a prop driven by presence (a type naming
 * `inside`, never lowered), stand at the prop's own anchor.
 *
 * @param prop The placed prop.
 * @param context The compile context, which names the prop's type and sizes the placed frame.
 * @param tileSize Tile size in pixels.
 * @param frameWidth The pixel width of the frame the shown state rests on.
 * @returns The anchor in tiles.
 */
export function thingAnchor(prop: PropInstance, context: CompileContext, tileSize: number, frameWidth: number): { readonly x: number; readonly y: number } {
  if (context.objectTypes.find((t) => t.type === prop.behaviour?.type)?.inside !== undefined) {
    return { x: prop.x, y: prop.y };
  }
  const initialWidth = frameSizeOf(context, frameId(prop.frame.tileset, null, prop.frame.tile))?.w ?? 0;
  if (initialWidth === frameWidth) {
    return { x: prop.x, y: prop.y };
  }
  return drawnFrameAnchor(propFeet(prop, initialWidth, tileSize), frameWidth, undefined, tileSize);
}

/**
 * The footprint of a behaviour prop in its initial state, for the editor's collision overlay.
 *
 * @param prop The placed prop.
 * @param context The compile context.
 * @param tileSize Tile size in pixels.
 * @returns The footprint, or null when the prop carries no behaviour, names an unregistered type or stamps
 *   nothing in its initial state.
 * @throws Error when the initial state's cells cannot be sized.
 */
export function initialFootprint(prop: PropInstance, context: CompileContext, tileSize: number): PropFootprint | null {
  const type = context.objectTypes.find((t) => t.type === prop.behaviour?.type);
  return type === undefined ? null : stateFootprint(prop, context, tileSize, type.initial);
}

/**
 * The footprint of a behaviour prop in any of its states, for the editor's collision overlay when a state is
 * chosen to be shown (ADR-040 amendment of 2026-10-03, P1.6-T74): the frame the state rests on, under the
 * type's policy for the state, exactly as the compile resolves it.
 *
 * @param prop The placed prop.
 * @param context The compile context.
 * @param tileSize Tile size in pixels.
 * @param stateId The state.
 * @returns The footprint, or null when the prop carries no behaviour, names an unregistered type, the type
 *   declares no such state, or the state stamps nothing.
 * @throws Error when a further state has no frame, or its cells cannot be sized.
 */
export function stateFootprint(prop: PropInstance, context: CompileContext, tileSize: number, stateId: string): PropFootprint | null {
  const type = context.objectTypes.find((t) => t.type === prop.behaviour?.type);
  const state = type?.states.find((s) => s.id === stateId);
  if (type === undefined || state === undefined) {
    return null;
  }
  const frame = frameOf(prop, type, state.id);
  const id = frameId(frame.tileset, null, frame.tile);
  return footprintOf(prop, state, id, context.collision.footprints?.[id], frameSizeOf(context, id), tileSize / COLLISION_CELLS_PER_TILE);
}

/**
 * The default activation ring of a behaviour prop in its initial state, for the editor to show behind the
 * cells an author paints: the ring the compiler grows (`defaultActivation`), spanning the footprints of the
 * further states that already have a frame. A further state with no frame yet, or whose cells would lie on a
 * frame the context cannot size, is left out rather than refused, since the editor reports the first as an
 * issue on its own and the compile names the second.
 *
 * @param prop The placed prop.
 * @param context The compile context.
 * @param tileSize Tile size in pixels.
 * @returns The cells in the frame's own unmirrored space, empty when the prop carries no behaviour, names
 *   an unregistered type or stamps nothing in its initial state.
 * @throws Error when the initial state's cells cannot be sized.
 */
export function initialDefaultActivation(prop: PropInstance, context: CompileContext, tileSize: number): (readonly [number, number])[] {
  const type = context.objectTypes.find((t) => t.type === prop.behaviour?.type);
  if (type === undefined) {
    return [];
  }
  const footprints = context.collision.footprints ?? {};
  const cellPixels = tileSize / COLLISION_CELLS_PER_TILE;
  const others = type.states.flatMap((state) => {
    const frame = state.id === type.initial ? undefined : prop.behaviour?.frames?.[state.id];
    if (frame === undefined) {
      return [];
    }
    const id = frameId(frame.tileset, null, frame.tile);
    const size = frameSizeOf(context, id);
    return resolvable(state, size) ? [footprintOf(prop, state, id, footprints[id], size, cellPixels)] : [];
  });
  return defaultActivation(initialFootprint(prop, context, tileSize), tileSize, COLLISION_CELLS_PER_TILE, others);
}

/**
 * Resolves every behaviour prop of a world into the appearance table, in document order so an unchanged
 * document compiles to identical bytes. A prop layer's compiled index counts the map's layers before it that
 * a chunk keeps, collision layers folding into the grid as they do for the compiled Players band, and a
 * thing's tie key is keyed over its whole layer, as the chunk keys the props it keeps (P1.6-T42).
 *
 * @param world The world document.
 * @param context The compile context, for the object types, the derived footprints, the frame sizes, the
 *   one-shot strips and the server's grain.
 * @param included Whether a map's props enter the table; a publish compile leaves out the maps it writes no chunk
 *   for (ADR-109 decision 9). Every map by default.
 * @returns The table and the index each behaviour prop takes.
 * @throws Error when a behaviour names a type the game did not register, a state's cells cannot be sized,
 *   an animation names a frame that starts no one-shot strip, or the world needs more appearances than the
 *   snapshot's field indexes.
 */
export function buildAppearances(world: WorldDocument, context: CompileContext, included: (map: MapDocument) => boolean = () => true): AppearanceTable {
  const strips = context.transitionStrips ?? {};
  const appearances: ManifestObjectAppearance[] = [];
  const byKey = new Map<string, number>();
  const indexOf = new Map<PropInstance, number>();
  for (const map of world.maps) {
    if (!included(map)) {
      continue;
    }
    let compiledIndex = -1;
    for (const layer of map.layers) {
      if (layer.kind !== "collision") {
        compiledIndex += 1;
      }
      if (layer.kind !== "prop") {
        continue;
      }
      const keys = tieKeys(layer.props, (prop) => context.objectTypes.find((t) => t.type === prop.behaviour?.type)?.inside !== undefined);
      for (const [i, prop] of layer.props.entries()) {
        const behaviour = prop.behaviour;
        if (behaviour === undefined) {
          continue;
        }
        const type = context.objectTypes.find((t) => t.type === behaviour.type);
        if (type === undefined) {
          throw new Error(`prop ${prop.id} carries behaviour ${behaviour.type}, which the game did not register; switch it to None or another behaviour in its Behaviour section`);
        }
        const appearance = appearanceOf(prop, type, context, strips, world.tileSize, compiledIndex, keys[i] as number);
        const key = appearanceKey(appearance);
        let index = byKey.get(key);
        if (index === undefined) {
          index = appearances.length;
          if (index > MAX_APPEARANCE) {
            throw new Error(`world ${world.id} needs more than ${MAX_APPEARANCE + 1} object appearances; the snapshot indexes at most that many`);
          }
          appearances.push(appearance);
          byKey.set(key, index);
        }
        indexOf.set(prop, index);
      }
    }
  }
  return { appearances, indexOf };
}
